import Swiper from "swiper"
import { Navigation } from "swiper/modules"
import lightGallery from "lightgallery";
import lgVideo from 'lightgallery/plugins/video'
import lgZoom from "lightgallery/plugins/zoom";
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import { setCookie, getCookie } from "./utils/Cookies"
import Alpine from "alpinejs"

declare global {
    const offerFinancing: Object[]
}

const offersActiveTabsStorage = {
    getItem(key) {
        let activeTabs = sessionStorage.getItem('offersActiveTabs')
        if (!activeTabs) return null

        activeTabs = JSON.parse(activeTabs)
        return activeTabs[key] ?? null
    },
    setItem(key, value) {
        const activeTabs = {...(JSON.parse(sessionStorage.getItem('offersActiveTabs') ?? '{}')), [`${key}`]: value }
        sessionStorage.setItem('offersActiveTabs', JSON.stringify(activeTabs))
    }
}

export function OfferSingle(offerId) {
    return {
        activeSection: null,
        sections: [],
        section: {
            ['x-init']() { this.sections.push({ 'id': this.$el.id, 'title': this.$el.dataset.sectionTitle }) },
            ['x-intersect.margin.-48%']() { this.activeSection = this.$el.id }
        },
        sectionsNav: {},
        sectionsNavLink: {
            [':href']() {
                return `#${this.offerSection.id}`
            },
            [':class']() {
                return { 'is-active': this.offerSection.id === this.activeSection }
            },
            ['x-html']() {
                return `<span>${this.offerSection.title}</span>`
            }
        },

        filterUrl: Alpine.$persist('').as('offersFilterUrl').using(sessionStorage),

        activeTab: Alpine.$persist('form').as(`${offerId}`).using(offersActiveTabsStorage),
        get activeTabIndex() {
            const tabs = this.$refs?.tabs
            if (!tabs) return 0
            return [...tabs.children].indexOf(tabs.querySelector(`button[data-name='${this.activeTab}']`)) ?? 0
        },
        activeTabIndicator: {
            [':style']() { return { '--offerTabsCount': this.$el.dataset.tabsCount ?? 2, '--offerTabsActive': this.activeTabIndex }}
        },
        tabToggle: {
            [':class']() { return this.activeTab === this.$el.dataset.name && 'is-active' },
            ['@click.prevent']() { this.activeTab = this.$el.dataset.name }
        },
        tabContent: {
            [':class']() { return this.activeTab === this.$el.dataset.name && 'is-active' }
        },
        copyOfferId: {
            [':class']() { return this.offerIdCopied && 'is-copied' },
            ['@click']() {
                const id = this.$el.querySelector('[data-variant-slot="offerID"]')?.textContent.trim()
                if (!id) return

                navigator.clipboard.writeText(id).then(() => {
                    this.offerIdCopied = true
                    clearTimeout(this.offerIdCopiedTimer)
                    this.offerIdCopiedTimer = setTimeout(() => this.offerIdCopied = false, 4000)
                })
            }
        },
        offerIdCopied: false,
        offerIdCopiedTimer: null,
        changeActiveTab: {
            ['@click']() {
                const targetTab = this.$el.dataset.tabName
                if (!targetTab) return

                (this.$el.dataset?.open ?? '') === 'financing' && this.$dispatch('open-financing')
                this.activeTab = targetTab
            }
        }
    }
}

type OperakVariant = {
    ID: number,
    offerID: string,
    length: number,
    mileage: number,
    mileageLabel: string,
    lengthLabel: string,
    price: string,
    priceWithoutVAT: string,
    priceNumeric: number,
    priceWithoutVATNumeric: number,
    url: string,
}

export function OperakOfferSingle(offerId: number) {
    let variants: OperakVariant[] = []
    try {
        const json = document.getElementById('operak-variants-data')?.textContent
        const parsed = json ? JSON.parse(json) : []
        if (Array.isArray(parsed)) variants = parsed as OperakVariant[]
    } catch (e) { variants = [] }

    const variantsById = new Map<number, OperakVariant>(variants.map(v => [v.ID, v]))
    
    const own = {
        variants,
        currentId: offerId,

        init() {
            history.replaceState({ variantId: this.currentId }, '')
            window.addEventListener('popstate', (e: PopStateEvent) => {
                const id = (e.state as { variantId?: number } | null)?.variantId
                if (id && variantsById.has(id) && id !== this.currentId) this.applyVariant(id, false)
            })
        },

        switchTo(rawId: string | number) {
            const id = typeof rawId === 'number' ? rawId : parseInt(rawId, 10)
            if (!id || id === this.currentId || !variantsById.has(id)) return
            this.applyVariant(id, true)
        },

        variantSelectChange: {
            ['@change'](e: Event) {
                const value = (e.target as HTMLSelectElement).value
                this.switchTo(value)
            }
        },

        // Modifier/aux clicks fall through so a variant can open in a new tab.
        variantRowClick: {
            ['@click'](e: MouseEvent) {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
                e.preventDefault()
                const row = (e.currentTarget as HTMLElement).closest<HTMLElement>('[data-variant-id]')
                const id = parseInt(row?.dataset.variantId ?? '0', 10)
                this.switchTo(id)
            }
        },

        applyVariant(id: number, pushHistory: boolean) {
            const variant = variantsById.get(id)
            if (!variant) return

            const run = () => {
                this.currentId = id

                document.querySelectorAll<HTMLElement>('[data-variant-slot]').forEach(el => {
                    const slot = el.dataset.variantSlot as keyof OperakVariant | undefined
                    if (!slot) return
                    const value = (variant as any)[slot]
                    if (value != null) el.textContent = String(value)
                })

                document.querySelectorAll<HTMLElement>('[data-variant-id]').forEach(row => {
                    const isCurrent = parseInt(row.dataset.variantId ?? '0', 10) === id
                    row.classList.toggle('is-current', isCurrent)
                    if (isCurrent) row.setAttribute('aria-current', 'true')
                    else row.removeAttribute('aria-current')
                    const check = row.querySelector<HTMLElement>('[data-variant-check]')
                    if (check) check.toggleAttribute('hidden', !isCurrent)
                })

                this.rebuildParameterSelects()
                this.syncFormFields(variant)

                if (pushHistory) history.pushState({ variantId: id }, '', variant.url)
                else history.replaceState({ variantId: id }, '', variant.url)
            }

            const vt = (document as any).startViewTransition
            if (typeof vt === 'function') vt.call(document, run)
            else run()
        },

        rebuildParameterSelects() {
            const current = variantsById.get(this.currentId)
            if (!current) return

            // Hero toggles and the lead-form selects share the same data-variant-select hooks; keep every copy in sync.
            const render = (selector: string, opts: OperakVariant[], labelKey: 'mileageLabel' | 'lengthLabel') => {
                const html = opts.map(o =>
                    `<option value="${o.ID}"${o.ID === current.ID ? ' selected' : ''}>${escapeHtml(o[labelKey])}</option>`
                ).join('')
                document.querySelectorAll<HTMLSelectElement>(selector).forEach(sel => { sel.innerHTML = html })
            }

            render(
                'select[data-variant-select="najezd"]',
                variants.filter(v => v.length === current.length).sort((a, b) => a.mileage - b.mileage),
                'mileageLabel'
            )
            render(
                'select[data-variant-select="delka_pronajmu"]',
                variants.filter(v => v.mileage === current.mileage).sort((a, b) => a.length - b.length),
                'lengthLabel'
            )
        },

        // Re-point the lead / test-drive forms at the selected variant. The CRM keys on the ID
        // (Source URL follows the history.pushState referer); the switcher selects never submit.
        syncFormFields(variant: OperakVariant) {
            document.querySelectorAll<HTMLInputElement>('input[name="vozidlo-id"]')
                .forEach(el => { el.value = variant.offerID })

            document.querySelectorAll<HTMLInputElement>('input[name="vozidlo"], input[name="vuz"]').forEach(el => {
                const title = el.value.split(' - ').slice(1).join(' - ')
                el.value = title ? `${variant.offerID} - ${title}` : variant.offerID
            })

            document.querySelectorAll<HTMLInputElement>('input[name="cena-vozidla"]')
                .forEach(el => { el.value = String(variant.priceWithoutVATNumeric) })
        }
    }
    
    return Object.defineProperties(own, Object.getOwnPropertyDescriptors(OfferSingle(offerId)))
}

function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
}

type OfferColor = {
    sId: number
    label: string
    swatch: string
    hero: string
    heroUrl: string
    photos: { ID: number, url: string, thumb: string }[]
}

function readOfferColors(): { colors: OfferColor[], primary: number } {
    const el = document.getElementById('offer-colors-data')
    if (!el) return { colors: [], primary: 0 }
    try {
        const parsed = JSON.parse(el.textContent || '[]')
        return { colors: Array.isArray(parsed) ? parsed as OfferColor[] : [], primary: parseInt(el.dataset.primary || '0', 10) }
    } catch { return { colors: [], primary: 0 } }
}

const attr = (s: string): string => String(s).replace(/"/g, '&quot;')

export default function () {

    document.addEventListener('alpine:init', () => {
        Alpine.data('OfferSingle', OfferSingle)
        Alpine.data('OperakOfferSingle', OperakOfferSingle)

        document.getElementById('offer-colors-data') && Alpine.store('offerColor', {
            colors: [] as OfferColor[],
            byId: new Map<number, OfferColor>(),
            committedId: 0,
            previewId: 0,
            open: false,

            init() {
                const { colors, primary } = readOfferColors()
                this.colors = colors
                this.byId = new Map(colors.map(c => [c.sId, c]))
                this.committedId = this.byId.has(primary) ? primary : (colors[0]?.sId ?? 0)
                this.previewId = this.committedId
            },

            get committed(): OfferColor | null { return this.byId.get(this.committedId) ?? null },
            get preview(): OfferColor | null { return this.byId.get(this.previewId) ?? null },
            get display(): OfferColor | null { return this.byId.get(this.open ? this.previewId : this.committedId) ?? null },
            get committedLabel(): string { return this.committed?.label ?? '' },
            get previewLabel(): string { return this.preview?.label ?? '' },

            toggle() { this.open ? this.close() : this.openPanel() },
            openPanel() { this.previewId = this.committedId; this.open = true },
            close() { this.open = false; this.previewId = this.committedId },
            select(sId: number) { if (this.byId.has(sId)) this.previewId = sId },
            confirm() { if (this.byId.has(this.previewId)) this.committedId = this.previewId; this.open = false },
            choose(sId: number) { if (this.byId.has(sId)) { this.committedId = sId; this.previewId = sId } },
        })

        // Fixed panel anchored to the trigger to escape the hero's overflow clip.
        Alpine.data('OfferColorPicker', () => ({
            reposition() {
                const t = this.$refs.trigger as HTMLElement, p = this.$refs.panel as HTMLElement
                if (!t || !p) return
                const r = t.getBoundingClientRect(), m = 8
                const left = Math.max(m, Math.min(r.left - 8, window.innerWidth - p.offsetWidth - m))
                const bottom = Math.max(m, Math.min(window.innerHeight - r.bottom, window.innerHeight - p.offsetHeight - m))
                p.style.left = `${left}px`
                p.style.bottom = `${bottom}px`
                p.style.top = 'auto'
                p.style.right = 'auto'
            }
        }))

        Alpine.data('OfferFeatures', () => ({
            allOpened: false,
            allFeaturesNoContent: false,
            visibleFeatures: 8,
            get featuresWithContent() {
                return this.$refs.features?.querySelectorAll('details:not(.no-content)') ?? []
            },
            featureSummary: {
                ['@click']() {
                    this.$nextTick(() => {
                        this.allOpened = Array.from(this.featuresWithContent)
                            .every((feature: HTMLDetailsElement) => feature.open)
                    })
                }
            },
            openAllFeatures: {
                ['x-show']() { return this.featuresWithContent.length },
                [':class']() { return this.allOpened && 'is-opened' },
                ['@click.prevent']() {
                    this.allOpened = !this.allOpened
                    this.featuresWithContent.forEach((feature: HTMLDetailsElement) => { feature.open = this.allOpened })
                    this.visibleFeatures = this.$refs.features.dataset.count
                }
            },
            showAllFeatures: {
                ['@click']() { this.visibleFeatures = this.$refs.features.dataset.count }
            }
        }))

        Alpine.data('OfferInterestForm', () => ({
            financingData: undefined,
            financingOptions: undefined,
            financovani: true,
            financovaniTyp: 0,
            financovaniAkontace: 0,
            financovaniNajezd: 0,
            financovaniDelka: 0,
            financovaniPosledniSplatka: 0,
            financovaniPojisteni: 'ano',
            financovaniPlatceDph: 'ano',
            protiucet: true,

            compareVariant(variant) {
                return parseInt(variant.n_typ_financovani) === this.financovaniTyp
                    && parseInt(variant.n_delka) === this.financingOptions.delka[this.financovaniDelka]
                    && ((this.financovaniTyp === 0 && parseInt(variant.n_akontace_procento) === this.financingOptions.akontace[this.financovaniAkontace])
                        || (this.financovaniTyp === 1 && parseInt(variant.n_rocni_najezd) === this.financingOptions.najezd[this.financovaniNajezd]))
            },

            get financingResult() {
                return this.financingData?.find(variant => {
                    return this.compareVariant(variant) && (this.financovaniTyp === 1 || parseInt(variant.n_PNS_procento) === this.financingOptions.posledniSplatka[this.financovaniPosledniSplatka])
                })
            },

            get financingPayment() {
                return this.financingResult ? parseInt(this.financingResult[this.financovaniPojisteni !== 'ano' || this.financovaniTyp === 1 ? 'n_splatka'
                    : this.financovaniPlatceDph !== 'ano' ? 'n_splatka_2' : 'n_splatka_3']) : 0;
            },

            get financingLastPayment() {
                return this.financingResult ? parseInt(this.financingResult['n_PNS_castka']) : 0;
            },

            get financovaniVysledek() {
                const result: Record<string, string> = {};
                if (this.financovaniTyp === 0) {
                    result.akontace = `${parseInt(this.financingResult?.n_akontace_castka ?? '0').toLocaleString()} Kč`
                    result['posledni-splatka'] = `${parseInt(this.financingResult?.n_PNS_castka ?? '0').toLocaleString()} Kč`
                } else if (this.financovaniTyp === 1) {
                    result.najezd = `${(this.financingOptions?.najezd[this.financovaniNajezd]).toLocaleString()} km`
                }

                return JSON.stringify({
                    typ: this.financovaniTyp === 0 ? 'Úvěr' : 'Operativní leasing',
                    ...result,
                    doba: `${this.financingOptions?.delka[this.financovaniDelka]} měsíců`,
                    splatka: `${this.financingPayment.toLocaleString()} Kč`
                })
            },

            setupFinancingBasedOnType() {
                this.financingOptions = { typ: [], akontace: [], najezd: [], delka: [], posledniSplatka: [] }

                this.financingData.forEach(variant => {
                    const variantValues = {
                        typ: parseInt(variant.n_typ_financovani),
                        najezd: parseInt(variant.n_rocni_najezd),
                        akontace: parseInt(variant.n_akontace_procento),
                        delka: parseInt(variant.n_delka)
                    }

                    Object.entries(variantValues).forEach(([key, value]) => {
                        !this.financingOptions[key].includes(value) && this.financingOptions[key].push(value)
                    })
                })

                Object.entries(this.financingOptions).forEach(([key, options]) => this.financingOptions[key].sort())

                this.financovaniAkontace = Math.max(this.financingOptions.akontace.length - 1, 0)
                this.$dispatch('range-change', { name: 'akontace' })

                this.financovaniDelka = Math.max(this.financingOptions.delka.length - 1, 0)
                this.$dispatch('range-change', { name: 'doba' })
            },

            init() {
                if(!offerFinancing || (offerFinancing && !offerFinancing.length))
                    return

                this.financingData = offerFinancing
                this.setupFinancingBasedOnType()

                this.financovaniTyp = this.financingOptions.typ[0]

                Alpine.effect(() => {
                    const availableOptions = this.financingData?.filter(variant => this.compareVariant(variant))
                        .map(variant => parseInt(variant.n_PNS_procento)).sort()

                    this.financingOptions.posledniSplatka = [];
                    (availableOptions ?? []).forEach(option => {
                        !this.financingOptions['posledniSplatka'].includes(option) && this.financingOptions['posledniSplatka'].push(option)
                    })

                    if (this.financingOptions.posledniSplatka.length && this.financingOptions.posledniSplatka.length < this.financovaniPosledniSplatka + 1) {
                        this.financovaniPosledniSplatka = 0
                        this.$dispatch('range-change', { name: 'posledni-splatka' })
                    }

                    this.$dispatch('range-change')
                })
            },

            toggleFinancovaniTyp: {
                [':disabled']() { return !this.financingOptions.typ.includes(parseInt(this.$el.value)) }
            },

            rangeFinancovaniAkontace: {
                [':min']() { return 0 },
                [':max']() { return (this.financingOptions?.akontace.length ?? 1) - 1 },
                [':step']() { return 1 }
            },
            rangeFinancovaniAkontaceResult: {
                ['x-html']() { return `<strong>${parseInt(this.financingResult?.n_akontace_castka ?? '0').toLocaleString()}</strong> Kč` }
            },

            rangeFinancovaniNajezd: {
                [':min']() { return 0 },
                [':max']() { return (this.financingOptions?.najezd.length ?? 1) - 1 },
                [':step']() { return 1 }
            },
            rangeFinancovaniNajezdResult: {
                ['x-html']() { return `<strong>${(this.financingOptions?.najezd[this.financovaniNajezd]).toLocaleString()}</strong> km` }
            },

            rangeFinancovaniDelka: {
                [':min']() { return 0 },
                [':max']() { return (this.financingOptions?.delka.length ?? 1) - 1 },
                [':step']() { return 1 }
            },
            rangeFinancovaniDelkaResult: {
                ['x-html']() { return `<strong>${this.financingOptions?.delka[this.financovaniDelka]}</strong> měsíců` }
            },

            rangeFinancovaniPosledniSplatka: {
                [':min']() { return 0 },
                [':max']() { return (this.financingOptions?.posledniSplatka.length ?? 1) - 1 },
                [':step']() { return 1 },
                [':disabled']() { return !(this.financingOptions?.posledniSplatka?.length > 1) },
            },
            rangeFinancovaniPosledniSplatkaResult: {
                ['x-html']() { return `<strong>${parseInt(this.financingResult?.n_PNS_castka ?? '0').toLocaleString()}</strong> Kč` }
            },

            financovaniToggle: {
                ['x-on:open-financing.window']() { this.financovani = true },
            },
            financingPaymentText: {
                ['x-text']() { return this.financingPayment.toLocaleString() }
            },
            financingPaymentTextAdd: {
                ['x-text']() { return this.financingLastPayment.toLocaleString() }
            }
        }));
    });

    (() => { // Photo gallery
        const gallery = document.querySelector('[data-gallery-swiper]') as HTMLElement
        window.openOfferGallery = (index) => {
            gallery && galleryInstance.openGallery(index);
        }

        if (!gallery) return;

        const galleryEl = gallery?.querySelector('.swiper-wrapper') as HTMLElement
        const galleryInstance = lightGallery(galleryEl, {
            licenseKey: "09B33A994193E54AB538C7C9C1853E23",
            speed: 400,
            enableDrag: false,
            download: false,
            getCaptionFromTitleOrAlt: false,
            mobileSettings: {
                showCloseIcon: true
            },
            plugins: [lgVideo, lgThumbnail, lgZoom]
        });

        let gallerySwiper: Swiper | null = null

        const colorsEl = document.getElementById('offer-colors-data')

        colorsEl && (() => {
            const heroLink = document.querySelector<HTMLAnchorElement>('.OfferHeroPhoto > a')
            const heroStrip = document.querySelector<HTMLElement>('.OfferHeroGallery')
            const videoEls = Array.from(galleryEl.querySelectorAll('.OfferGalleryItem--video'))
            const videoCount = videoEls.length
            const videoSlides = videoEls.map(el => el.outerHTML).join('')
            // Strip shape must track the isOperakBMW() branch in partials/offers/hero-gallery.blade.php.
            const operakStrip = colorsEl.dataset.strip === 'operak'
            const stripVideos = Array.from(heroStrip?.querySelectorAll('.OfferGalleryItem--video') ?? [])
                .map(el => el.outerHTML).join('')

            const thumb = (photo: OfferColor['photos'][number], index: number, active = false): string =>
                `<a href="${attr(photo.url)}" class="OfferGalleryItem${active ? ' is-active' : ''}" onclick="event.preventDefault();window.openOfferGallery(${index})"><figure>${photo.thumb}</figure></a>`

            /** Operak strip: hero photo leads, four tiles max, the fourth turning into a +N opener. */
            const operakThumbs = (color: OfferColor): string => {
                const hasMore = color.photos.length > 4
                let html = color.photos.slice(0, hasMore ? 3 : 4)
                    .map((p, i) => thumb(p, i + videoCount, i === 0)).join('')
                if (hasMore) {
                    html += `<a href="${attr(color.photos[3].url)}" class="OfferHeroGallery-more" onclick="event.preventDefault();window.openOfferGallery(${3 + videoCount})">+${color.photos.length - 3}</a>`
                }
                return html
            }

            /** Default strip: videos first, then photos 2..N (photo 1 is the hero), four tiles total. */
            const defaultThumbs = (color: OfferColor): string => {
                let html = stripVideos + color.photos.slice(1, 5 - videoCount)
                    .map((p, i) => thumb(p, i + 1 + videoCount)).join('')
                const total = color.photos.length + videoCount
                if (total > 5) {
                    html += `<a href="#galerie" class="OfferHeroGallery-more" onclick="window.openOfferGallery(4)">+${total - 4}</a>`
                }
                return html
            }

            const lightboxPhotos = (color: OfferColor): string => color.photos.map(p =>
                `<a href="${attr(p.url)}" class="OfferGalleryItem swiper-slide"><figure>${p.thumb}</figure></a>`
            ).join('')

            const rebuildGallery = (color: OfferColor) => {
                if (heroLink && color.hero) { heroLink.href = color.heroUrl; heroLink.innerHTML = color.hero }
                if (heroStrip) heroStrip.innerHTML = operakStrip ? operakThumbs(color) : defaultThumbs(color)
                galleryEl.innerHTML = videoSlides + lightboxPhotos(color)
                galleryInstance.refresh()
                // Only the default layout mounts a carousel; swapping its slides leaves Swiper's bookkeeping stale.
                gallerySwiper?.update()
                gallerySwiper?.slideTo(0, 0)
            }

            document.addEventListener('alpine:initialized', () => {
                const store = Alpine.store('offerColor') as { colors: OfferColor[], committedId: number, display: OfferColor | null }
                if (!store?.colors?.length) return
                let lastRendered = store.committedId
                Alpine.effect(() => {
                    const color = store.display
                    if (!color || color.sId === lastRendered) return
                    lastRendered = color.sId
                    rebuildGallery(color)
                })
            })
        })();

        if (gallery.hidden) return;

        gallerySwiper = new Swiper(gallery, {
            slidesPerView: 'auto',
            cssMode: true,
            modules: [ Navigation ],
            breakpoints: {
                768: {
                    slidesPerView: 3
                },
                1280: {
                    slidesPerView: 4
                },
                2048: {
                    slidesPerView: 5
                }
            },
            navigation: {
                nextEl: '[data-gallery-next]',
                prevEl: '[data-gallery-prev]',
                addIcons: false,
            },
        });

        gallery.addEventListener('click', e => {
            const target = e.target as HTMLElement
            if ( !target.matches('a.swiper-slide-duplicate[data-lg-id]') )
                return

            e.preventDefault()
            window.openOfferGallery(parseInt(target.dataset.swiperSlideIndex))
        })
    })();

    (() => { // Last viewed offers
        if (!document.body.classList.contains('single-offers'))
            return;

        const offerID = Array.from(document.body.classList).find(el => el.startsWith('postid-')).replace('postid-', '');

        let cookieArray = [];
        if (getCookie('LastViewedOffers') !== '') {
            cookieArray = getCookie('LastViewedOffers').split(';');
        }

        cookieArray = cookieArray.filter(offer => offer !== offerID);
        cookieArray.unshift(offerID);

        setCookie('LastViewedOffers', cookieArray.splice(0, 7).join(';'), 365);
    })();

    (() => { // Sticky summary bar
        const stickyBar = document.querySelector('[data-sticky-summary-bar]')
        if (!stickyBar || !document.body.classList.contains('is-mobile'))
            return

        const toggleStickyBar = () => {
            const startPoint = document.querySelector('.OfferHeroParameters')?.getBoundingClientRect().top,
                endPoint = document.querySelector('.NewsletterRow')?.getBoundingClientRect().top

            const sticked = startPoint < 0 && endPoint > window.innerHeight

            stickyBar.classList.toggle('is-sticked', sticked)
            document.body.style.setProperty('--offerBarOffset', `${sticked ? -80 : 0}px`)
        }

        window.addEventListener('scroll', () => toggleStickyBar(), { passive: true })
        toggleStickyBar()
    })();

    (async () => { // Sellers exposure countUp
        document.querySelectorAll('.OfferTabs-content[data-name="form"] .OfferSellerCard')
            .forEach(el => {
                const id = el.querySelector<HTMLElement>('[data-id]')?.dataset.id
                id && fetch(`/wp-json/team/seller-exposure/${id}`, { method: "POST" })
            })
    })();
}
